<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { XIcon } from 'lucide-vue-next'

const SCROLL_TRIGGER_ENABLED = true

const props = defineProps<{ zoneId: string; reviveId: string }>()
const config = useRuntimeConfig()

const adAttrs = computed(() => ({
  [`data-${config.public.reviveProduct}-zoneid`]: props.zoneId,
  [`data-${config.public.reviveProduct}-id`]: props.reviveId
}))

const duration = ref(5)
const isVisible = ref(false)
const intervalId = ref<NodeJS.Timeout | null>(null)

const hidePopup = () => {
  isVisible.value = false

  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }
}

const handleAdClick = () => hidePopup()

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    hidePopup()
  }
}

const waitForScroll = (): Promise<void> => {
  return new Promise((resolve) => {
    const check = () => {
      const scrolled = window.scrollY + window.innerHeight
      const total = document.documentElement.scrollHeight
      if (scrolled / total >= 0.1) {
        window.removeEventListener('scroll', check)
        resolve()
      }
    }
    window.addEventListener('scroll', check, { passive: true })
    check()
  })
}

const initializeAd = () => {
  duration.value = 5
  isVisible.value = false

  if (intervalId.value) {
    clearInterval(intervalId.value)
    intervalId.value = null
  }

  const showAd = () => {
    preloadAdImage()
      .then(() => {
        isVisible.value = true
        intervalId.value = setInterval(() => {
          if (duration.value > 0) {
            duration.value--
          } else {
            hidePopup()
          }
        }, 1000)
      })
      .catch((error) => {
        console.warn('Ad image failed to load, component hidden : ' + error)
      })
  }

  const isMobile = window.matchMedia('(max-width: 767px)').matches
  if (SCROLL_TRIGGER_ENABLED && !isMobile) {
    waitForScroll().then(showAd)
  } else {
    showAd()
  }
}

const waitForReviveScript = (): Promise<void> => {
  return new Promise((resolve) => {
    const checkRevive = () => {
      if (window.reviveAsync && window.reviveAsync[props.reviveId]) {
        // Force the "revive" script to detect and render the ad
        window.reviveAsync[props.reviveId].apply(window.reviveAsync[props.reviveId].detect())
        resolve()
      } else {
        setTimeout(checkRevive, 100)
      }
    }
    checkRevive()
  })
}

const preloadAdImage = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    nextTick(async () => {
      // Wait for the "revive" script to be ready
      await waitForReviveScript()

      // Additional delay to ensure ad is rendered
      await new Promise((resolve) => setTimeout(resolve, 500))

      const adElement = document.querySelector(
        `ins[data-${config.public.reviveProduct}-zoneid="${props.zoneId}"]`
      ) as HTMLElement

      if (!adElement) {
        reject('Ad element not found')
        return
      }

      const observer = new MutationObserver((mutations) => {
        mutations.some((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            checkForImage()
            return true
          }
        })
      })

      const checkForImage = (attempts = 0) => {
        if (attempts > 25) {
          observer.disconnect()
          reject('Image not found after multiple attempts')
          return
        }

        const adLink = adElement.querySelector('a')
        const adImg = adLink ? adLink.querySelector('img') : undefined

        if (adImg && adImg.src) {
          const img = new Image()

          img.onload = () => {
            resolve()
            observer.disconnect()
          }

          img.onerror = () => {
            reject('Failed to preload image')
            observer.disconnect()
          }

          img.src = adImg.src
        } else {
          setTimeout(() => checkForImage(attempts + 1), 200)
        }
      }

      observer.observe(adElement, {
        childList: true,
        subtree: true,
        attributes: true
      })

      checkForImage()
    })
  })
}

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey)
  initializeAd()
})

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<template>
  <div
    :class="
      isVisible
        ? 'opacity-100 md:translate-x-0'
        : 'pointer-events-none opacity-0 md:translate-x-full'
    "
    class="fixed inset-0 z-50 flex items-center justify-center overflow-hidden transition-all duration-500 md:inset-auto md:bottom-2 md:right-2 md:block"
    aria-label="fullscreen"
  >
    <div class="p-3">
      <div class="relative overflow-hidden rounded-md text-xs shadow-lg" @click="handleAdClick">
        <ins v-bind="adAttrs" />
        <div
          class="pointer-events-none absolute right-10 top-0 flex h-10 select-none items-center whitespace-nowrap rounded-bl-md bg-white/80 px-4 leading-none"
        >
          <span>{{ duration }}</span
          >{{ $t('widget.revive_ad.fullscreen.seconds_to_close') }}
        </div>
        <button
          class="absolute right-0 top-0 z-10 flex size-10 items-center justify-center bg-white/90 hover:[&>svg]:rotate-90"
          aria-label="Close"
          @click="hidePopup"
        >
          <XIcon class="size-6 transition" />
        </button>
      </div>
    </div>
  </div>
</template>
